/**
 * TagBadge — small, colored, optionally removable badge for a single tag.
 *
 * Rendering rules (i18n-aware):
 *  1. The tag is identified by its STABLE slug (e.g. `new`, `shortlisted`).
 *  2. Display label resolution order:
 *     a. i18n key `tags.{slug}` if present (covers all default tags FR+EN).
 *     b. The registry entry's `name` (custom user-created tags).
 *     c. The slug itself (defensive fallback).
 *  3. The badge colour is driven by the tag's category from the registry.
 *
 * Caller may pass a raw slug string (compact API) OR a tag object
 * { slug, name, category }. Both work.
 */
import React from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const CATEGORY_STYLES = {
    pipeline: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
    action: 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100',
    priority: 'bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100',
    availability: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
    matching: 'bg-cyan-50 text-cyan-700 border-cyan-200 hover:bg-cyan-100',
    custom: 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200',
};

const REMOVE_STYLES = {
    pipeline: 'hover:text-blue-900',
    action: 'hover:text-orange-900',
    priority: 'hover:text-violet-900',
    availability: 'hover:text-emerald-900',
    matching: 'hover:text-cyan-900',
    custom: 'hover:text-slate-900',
};

export function findTagBySlug(slug, registry = []) {
    if (!slug || !registry?.length) return null;
    return registry.find((t) => t?.slug === slug) || null;
}

export function getCategoryFor(slug, registry = []) {
    const found = findTagBySlug(slug, registry);
    return found?.category || 'custom';
}

export default function TagBadge({
    slug,
    registry = [],
    category,
    onRemove,
    size = 'sm',
    className = '',
    'data-testid': testId,
}) {
    const { t, i18n } = useTranslation();
    if (!slug) return null;

    const registryEntry = findTagBySlug(slug, registry);
    const cat = category || registryEntry?.category || 'custom';
    // i18n lookup with two-tier fallback: registry name → raw slug.
    const fallback = registryEntry?.name || slug;
    const label = i18n.exists(`tags.${slug}`) ? t(`tags.${slug}`) : fallback;

    const style = CATEGORY_STYLES[cat] || CATEGORY_STYLES.custom;
    const removeStyle = REMOVE_STYLES[cat] || REMOVE_STYLES.custom;
    const sizeCls = size === 'xs'
        ? 'text-[10px] px-1.5 py-0.5'
        : 'text-xs px-2 py-0.5';

    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full border ${style} ${sizeCls} font-medium transition-colors ${className}`}
            data-testid={testId || `tag-badge-${slug}`}
        >
            <span className="truncate max-w-[160px]">{label}</span>
            {onRemove && (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onRemove(slug); }}
                    className={`flex items-center justify-center rounded-full -mr-0.5 ${removeStyle}`}
                    aria-label={`Remove ${label}`}
                    data-testid={`tag-remove-${slug}`}
                >
                    <X className="w-3 h-3" />
                </button>
            )}
        </span>
    );
}

export { CATEGORY_STYLES };
